import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { RebusSessionProvider, useRebusSession } from '../context/RebusSessionContext';
import { getRebus } from '../services/rebusService';
import RebusJoinScreen from '../components/rebus/team/RebusJoinScreen';
import StationView from '../components/rebus/team/StationView';
import ExtraTaskList from '../components/rebus/team/ExtraTaskList';
import TeamProgress from '../components/rebus/team/TeamProgress';

const ChallengeReporter = lazy(() => import('../components/rebus/team/ChallengeReporter'));
const PhotoFeed = lazy(() => import('../components/rebus/team/PhotoFeed'));

/**
 * Top-level page for rebus team participation.
 * Routes:
 *   /rebus/join          — empty PIN entry
 *   /rebus/team/:sessionId — pre-filled PIN from URL
 */
const STORAGE_KEY_PREFIX = 'mikkelchase_team_';

export default function RebusTeamPage() {
  const { sessionId: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const prefilledPin = urlSessionId || searchParams.get('session') || searchParams.get('pin') || '';
  const [joined, setJoined] = useState(false);
  const [sessionId, setSessionId] = useState(prefilledPin);
  const [teamId, setTeamId] = useState('');
  const [recovering, setRecovering] = useState(true);

  // Try to recover session from localStorage on mount
  useEffect(() => {
    try {
      // Check if we have a saved session for this PIN/sessionId
      const key = prefilledPin ? `${STORAGE_KEY_PREFIX}${prefilledPin}` : null;
      if (key) {
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed?.sessionId === 'string' && typeof parsed?.teamId === 'string') {
            setSessionId(parsed.sessionId);
            setTeamId(parsed.teamId);
            setJoined(true);
          }
        }
      } else if (prefilledPin === '') {
        // No prefilled PIN — check all stored sessions for a match
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(STORAGE_KEY_PREFIX)) {
            try {
              const saved = JSON.parse(localStorage.getItem(k));
              if (typeof saved?.sessionId === 'string' && typeof saved?.teamId === 'string') {
                setSessionId(saved.sessionId);
                setTeamId(saved.teamId);
                setJoined(true);
                break;
              }
            } catch {
              // Remove corrupt entry
              localStorage.removeItem(k);
            }
          }
        }
      }
    } catch {
      // Clear potentially corrupt localStorage data
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith(STORAGE_KEY_PREFIX)) localStorage.removeItem(k);
        }
      } catch { /* ignore */ }
    }
    setRecovering(false);
  }, [prefilledPin]);

  const handleJoin = ({ sessionId: sid, teamId: tid }) => {
    setSessionId(sid);
    setTeamId(tid);
    setJoined(true);
    // Persist to localStorage
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${sid}`, JSON.stringify({ sessionId: sid, teamId: tid }));
    } catch {
      // Ignore storage errors
    }
  };

  const handleLeave = () => {
    try {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${sessionId}`);
    } catch {
      // Ignore
    }
    setJoined(false);
    setTeamId('');
  };

  if (recovering) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-display">
        <span className="material-symbols-outlined text-5xl text-teal-500 animate-spin mb-4">progress_activity</span>
        <p className="text-gray-600 font-semibold">Lastar...</p>
      </div>
    );
  }

  if (!joined) {
    return (
      <RebusJoinScreen
        onJoin={handleJoin}
        prefilledPin={prefilledPin}
      />
    );
  }

  return (
    <RebusSessionProvider sessionId={sessionId}>
      <RebusTeamApp sessionId={sessionId} teamId={teamId} onLeave={handleLeave} />
    </RebusSessionProvider>
  );
}

/* ------------------------------------------------------------------ */

const TAB_STATIONS = 'stations';
const TAB_EXTRA = 'extra';
const TAB_PROGRESS = 'progress';
const TAB_CHALLENGE_REPORT = 'challenge_report';
const TAB_PHOTOS = 'photos';

/**
 * Returns tasks in random but consistent order per team when taskOrder is 'random'.
 * Uses teamId as a seed so each team gets a unique but stable ordering.
 */
const getOrderedTasks = (tasks, taskOrder, teamId) => {
  if (taskOrder !== 'random' || !tasks) return tasks;
  const seeded = [...tasks];
  let seed = teamId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  for (let i = seeded.length - 1; i > 0; i--) {
    seed = (seed * 16807) % 2147483647;
    const j = seed % (i + 1);
    [seeded[i], seeded[j]] = [seeded[j], seeded[i]];
  }
  return seeded;
};

/**
 * Inner component wrapped in RebusSessionProvider.
 * Manages tab navigation and rebus data loading.
 */
function RebusTeamApp({ sessionId, teamId, onLeave }) {
  const { session, loading: sessionLoading, error: sessionError } = useRebusSession();
  const [activeTab, setActiveTab] = useState(TAB_STATIONS);
  const [rebus, setRebus] = useState(null);
  const [rebusLoading, setRebusLoading] = useState(true);
  const [rebusError, setRebusError] = useState(null);
  const [selectedStationId, setSelectedStationId] = useState(null);
  const defaultStationSetRef = useRef(false);

  // Fetch rebus data when session loads
  useEffect(() => {
    if (!session?.rebusId) return;

    let cancelled = false;
    setRebusLoading(true);

    getRebus(session.rebusId)
      .then((data) => {
        if (!cancelled) {
          setRebus(data);
          setRebusError(null);
          // Default to first station (only once)
          if (data.stations?.length > 0 && !defaultStationSetRef.current) {
            setSelectedStationId(data.stations[0].id);
            defaultStationSetRef.current = true;
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setRebusError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setRebusLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.rebusId]);

  const handleStationComplete = useCallback((stationId) => {
    if (!rebus?.stations) return;
    // Auto-advance to next station
    const currentIndex = rebus.stations.findIndex((s) => s.id === stationId);
    if (currentIndex >= 0 && currentIndex < rebus.stations.length - 1) {
      setSelectedStationId(rebus.stations[currentIndex + 1].id);
    }
  }, [rebus]);

  // Loading state
  if (sessionLoading || rebusLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-display">
        <span className="material-symbols-outlined text-5xl text-teal-500 animate-spin mb-4">progress_activity</span>
        <p className="text-gray-600 font-semibold">Lastar rebus...</p>
      </div>
    );
  }

  // Error state
  if (sessionError || rebusError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-display px-6">
        <span className="material-symbols-outlined text-5xl text-red-400 mb-4">error_outline</span>
        <p className="text-gray-800 font-bold text-lg mb-1">Noko gjekk gale</p>
        <p className="text-gray-500 text-sm text-center">{sessionError || rebusError}</p>
      </div>
    );
  }

  if (!session || !rebus) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-display">
        <span className="material-symbols-outlined text-5xl text-gray-300 mb-4">cloud_off</span>
        <p className="text-gray-500 font-semibold">Fann ikkje data</p>
      </div>
    );
  }

  const team = session.teams?.[teamId];

  // If team not found in session (e.g. removed), clean up and go back to join
  if (!team && session.teams && !sessionLoading) {
    // Use setTimeout to avoid setState during render
    setTimeout(() => onLeave?.(), 0);
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-display">
        <span className="material-symbols-outlined text-5xl text-gray-300 mb-4">group_off</span>
        <p className="text-gray-500 font-semibold">Laget blei ikkje funne</p>
      </div>
    );
  }
  // Rotate stations based on team's start station
  const allStations = rebus.stations || [];
  const startStationId = team?.startStationId;
  const stations = (() => {
    if (!startStationId || allStations.length === 0) return allStations;
    const startIndex = allStations.findIndex(s => s.id === startStationId);
    if (startIndex <= 0) return allStations;
    return [...allStations.slice(startIndex), ...allStations.slice(0, startIndex)];
  })();
  const extraTasks = rebus.extraTasks || [];
  const stationResults = team?.stationResults || {};
  const extraTaskResults = team?.extraTaskResults || {};
  const currentStation = stations.find((s) => s.id === selectedStationId) || null;
  const isStationLeader = team?.name?.toLowerCase() === 'stasjonsleiar';
  // Find the member name for this device (last one added, stored in localStorage)
  const storedMemberName = team?.members?.length > 0 ? team.members[team.members.length - 1] : '';

  // Use totalScore from Firestore (recalculated after each approval)
  const liveScore = team?.totalScore ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 font-display flex flex-col">
      {/* Top bar */}
      <div className="bg-white shadow-ambient px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
        <span className="material-symbols-outlined text-teal-600">explore</span>
        <h1 className="font-extrabold text-gray-900 truncate flex-1">{rebus.title}</h1>
        <div className="bg-teal-100 text-teal-700 font-bold text-sm px-2.5 py-1 rounded-full shrink-0">
          {liveScore} p
        </div>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Vil du forlate denne Mikkelchase-økta?')) {
              onLeave?.();
            }
          }}
          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-gray-100"
          title="Forlat økt"
        >
          <span className="material-symbols-outlined text-xl">logout</span>
        </button>
      </div>

      {/* Waiting banner */}
      {session.status === 'waiting' && (
        <div className="mx-4 mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <span className="material-symbols-outlined text-amber-500 text-xl shrink-0">hourglass_top</span>
          <p className="text-amber-800 text-sm font-semibold">
            Mikkelchase er ikkje starta enno. Vent på at admin startar før de leverer svar.
          </p>
        </div>
      )}

      {/* Tab content area */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-4">
        {activeTab === TAB_STATIONS && (
          <>
            {/* Station selector — horizontal scroll */}
            {stations.length > 1 && (
              <div className="mb-4 -mx-4 px-4">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {stations.map((station) => {
                    const isCompleted = !!stationResults[station.id]?.completedAt;
                    const isActive = station.id === selectedStationId;

                    return (
                      <button
                        key={station.id}
                        type="button"
                        onClick={() => setSelectedStationId(station.id)}
                        className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-95 ${
                          isActive
                            ? 'bg-teal-600 text-white shadow-md'
                            : isCompleted
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-white text-gray-700 shadow-ambient'
                        }`}
                      >
                        {isCompleted && (
                          <span className="material-symbols-outlined text-base">check_circle</span>
                        )}
                        <span>{station.name || `Stasjon ${station.order}`}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <StationView
              station={currentStation}
              teamId={teamId}
              sessionId={sessionId}
              stationResults={stationResults[currentStation?.id]}
              rebusData={rebus}
              onComplete={handleStationComplete}
            />
          </>
        )}

        {activeTab === TAB_EXTRA && (
          <ExtraTaskList
            extraTasks={getOrderedTasks(extraTasks, rebus.settings?.taskOrder, teamId)}
            teamId={teamId}
            sessionId={sessionId}
            extraTaskResults={extraTaskResults}
          />
        )}

        {activeTab === TAB_PROGRESS && (
          <TeamProgress
            rebus={rebus}
            session={session}
            teamId={teamId}
          />
        )}

        {activeTab === TAB_PHOTOS && (
          <Suspense fallback={<div className="py-12 text-center text-gray-500">Lastar...</div>}>
            <PhotoFeed session={session} sessionId={sessionId} />
          </Suspense>
        )}

        {activeTab === TAB_CHALLENGE_REPORT && (
          <Suspense fallback={<div className="py-12 text-center text-gray-500">Lastar...</div>}>
            <ChallengeReporter
              rebus={rebus}
              session={session}
              sessionId={sessionId}
              memberName={storedMemberName}
            />
          </Suspense>
        )}
      </div>

      {/* Bottom tab navigation — fixed */}
      <nav className="fixed bottom-0 inset-x-0 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40 safe-area-pb">
        <div className="flex items-stretch max-w-lg mx-auto">
          <TabButton
            active={activeTab === TAB_STATIONS}
            icon="location_on"
            label="Stasjonar"
            onClick={() => setActiveTab(TAB_STATIONS)}
          />
          <TabButton
            active={activeTab === TAB_EXTRA}
            icon="assignment"
            label="Ekstraoppgåver"
            badge={extraTasks.length > 0 ? `${Object.keys(extraTaskResults).length}/${extraTasks.length}` : null}
            badgeUrgent={extraTasks.length > 0 && Object.keys(extraTaskResults).length < extraTasks.length}
            onClick={() => setActiveTab(TAB_EXTRA)}
          />
          <TabButton
            active={activeTab === TAB_PHOTOS}
            icon="photo_library"
            label="Bilete"
            onClick={() => setActiveTab(TAB_PHOTOS)}
          />
          {isStationLeader && (
            <TabButton
              active={activeTab === TAB_CHALLENGE_REPORT}
              icon="emoji_events"
              label="Rapport"
              onClick={() => setActiveTab(TAB_CHALLENGE_REPORT)}
            />
          )}
          <TabButton
            active={activeTab === TAB_PROGRESS}
            icon="leaderboard"
            label="Poeng"
            onClick={() => setActiveTab(TAB_PROGRESS)}
          />
        </div>
      </nav>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Single bottom tab button.
 */
function TabButton({ active, icon, label, badge, badgeUrgent, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors ${
        active ? 'text-teal-600' : 'text-gray-400'
      }`}
    >
      <div className="relative">
        <span className={`material-symbols-outlined text-2xl ${active ? 'font-bold' : ''}`}>
          {icon}
        </span>
        {badge && (
          <span className={`absolute -top-1.5 -right-3 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
            badgeUrgent ? 'bg-red-500 animate-pulse' : 'bg-teal-500'
          }`}>
            {badge}
          </span>
        )}
      </div>
      <span className={`text-[11px] font-semibold ${active ? 'text-teal-700' : ''}`}>
        {label}
      </span>
    </button>
  );
}
